import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Plus, Edit, Trash2 } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  return {
    'Content-Type': 'application/json',
    'Authorization': token ? `Bearer ${token}` : ''
  };
};

const RackEntry = () => {
  const [racks, setRacks] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingRack, setEditingRack] = useState(null);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    rack_number: '',
    location: '',
    capacity: 50,
    is_active: true
  });

  useEffect(() => {
    fetchRacks();
  }, []);

  const fetchRacks = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/library/racks`, { headers: getAuthHeaders() });
      setRacks(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Error fetching racks:', error);
      setRacks([]);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const url = editingRack 
        ? `${API_URL}/api/library/racks/${editingRack.id}`
        : `${API_URL}/api/library/racks`;

      if (editingRack) {
        await axios.put(url, formData, { headers: getAuthHeaders() });
      } else {
        await axios.post(url, formData, { headers: getAuthHeaders() });
      }

      fetchRacks();
      handleCloseModal();
      alert('Rack saved successfully!');
    } catch (error) {
      console.error('Error saving rack:', error);
      alert(error.response?.data?.detail || 'Failed to save rack');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this rack?')) {
      return;
    }

    try {
      await axios.delete(`${API_URL}/api/library/racks/${id}`, { headers: getAuthHeaders() });
      fetchRacks();
      alert('Rack deleted successfully!');
    } catch (error) {
      console.error('Error deleting rack:', error);
      alert('Failed to delete rack');
    }
  };

  const handleEdit = (rack) => {
    setEditingRack(rack);
    setFormData({
      rack_number: rack.rack_number,
      location: rack.location,
      capacity: rack.capacity,
      is_active: rack.is_active
    });
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingRack(null);
    setFormData({
      rack_number: '',
      location: '',
      capacity: 50,
      is_active: true
    });
  };

  return (
    <div className="p-6 space-y-6">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Rack Management</CardTitle>
          <Button onClick={() => setIsModalOpen(true)}>
            <Plus className="w-4 h-4 mr-2" />
            Add Rack
          </Button>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr className="border-b">
                  <th className="text-left p-3">Rack Number</th>
                  <th className="text-left p-3">Location</th>
                  <th className="text-left p-3">Capacity</th>
                  <th className="text-left p-3">Status</th>
                  <th className="text-left p-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {racks.length === 0 ? (
                  <tr>
                    <td colSpan="5" className="text-center p-4 text-gray-500">
                      No racks found. Click "Add Rack" to create one.
                    </td>
                  </tr>
                ) : (
                  racks.map((rack) => (
                    <tr key={rack.id} className="border-b hover:bg-gray-50">
                      <td className="p-3 font-medium">{rack.rack_number}</td>
                      <td className="p-3">{rack.location}</td>
                      <td className="p-3">{rack.capacity} books</td>
                      <td className="p-3">
                        <span className={`px-2 py-1 rounded text-xs ${rack.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                          {rack.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="p-3">
                        <div className="flex gap-2">
                          <Button size="sm" variant="outline" onClick={() => handleEdit(rack)}>
                            <Edit className="w-3 h-3" />
                          </Button>
                          <Button size="sm" variant="destructive" onClick={() => handleDelete(rack.id)}>
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingRack ? 'Edit Rack' : 'Add New Rack'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">Rack Number *</label>
                <Input
                  required
                  value={formData.rack_number}
                  onChange={(e) => setFormData({ ...formData, rack_number: e.target.value })}
                  placeholder="e.g., A-01, B-12, C-05"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Capacity *</label>
                <Input
                  type="number"
                  required
                  min="1"
                  value={formData.capacity}
                  onChange={(e) => setFormData({ ...formData, capacity: parseInt(e.target.value) || 50 })}
                  placeholder="Number of books"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Location *</label>
              <Input
                required
                value={formData.location}
                onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                placeholder="e.g., Ground Floor - Section A, First Floor - Fiction"
              />
            </div>

            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="is_active"
                checked={formData.is_active}
                onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                className="w-4 h-4"
              />
              <label htmlFor="is_active" className="text-sm font-medium">Active</label>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={handleCloseModal}>
                Cancel
              </Button>
              <Button type="submit" disabled={loading}>
                {loading ? 'Saving...' : editingRack ? 'Update' : 'Create'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RackEntry;